import React, { useState, useEffect } from "react";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { Spin, Pagination } from "antd";
import dayjs from "dayjs";

const BusinessActivityLog = ({ orgId, businessName, dealCategoryId }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs, loading, totalRecords } = useSelector((state) => state.activityLog);

  useEffect(() => {
    if (orgId) {
      dispatch(getActivityLog({ module: "BUSINESS", entityId: orgId, page, limit, dealCategoryId }));
    }
  }, [dispatch, orgId, page, dealCategoryId]);

  const formatPayloadValue = (key, value, payload = {}) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "object") return JSON.stringify(value);

    if (key === "purpose") {
      let text = String(value).trim().replace(/\brecived\b/gi, "received");
      text = text.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
      if (payload.txnChannel) {
        text = `${text} (${String(payload.txnChannel).trim().toUpperCase()})`;
      }
      return text;
    }

    const strVal = String(value).trim();

    if (key === "investorTypeId" || key === "userTypeId" || key === "businessTypeId") {
      const USER_TYPE_MAP = {
        "UTY1000001": "Individual",
        "UTY1000002": "HUF",
        "UTY1000003": "Company",
        "UTY1000004": "Proprietorship",
        "UTY1000005": "Private Limited",
        "UTY1000006": "Company",
        "UTY1000007": "Partnership Firm / LLP",
        "UTY1000008": "NBFC",
      };
      return USER_TYPE_MAP[strVal] || strVal;
    }

    if (key === "discountRate" || key === "annualDiscountRate") {
      const cleanStr = strVal.replace(/%/g, "").trim();
      const num = parseFloat(cleanStr);
      if (!isNaN(num)) {
        const percentage = (num > 0 && num <= 1) ? num * 100 : num;
        const rounded = Number.isInteger(percentage) ? percentage : parseFloat(percentage.toFixed(2));
        return `${rounded}%`;
      }
      return strVal.endsWith("%") ? strVal : `${strVal}%`;
    }

    if (
      key === "amount" ||
      key === "creditExposure" ||
      key === "requestedCredit" ||
      key === "approvedCredit" ||
      key === "newCredit" ||
      key === "currentCredit" ||
      key === "creditLimit"
    ) {
      const num = Number(strVal.replace(/,/g, ""));
      if (!isNaN(num)) {
        return num.toLocaleString("en-IN");
      }
    }

    return strVal;
  };

  const DEPOSIT_PAYLOAD_ORDER = [
    'orgId',
    'transactionId',
    'businessName',
    'investorName',
    'virtualAccountNumber',
    'utr',
    'amount',
    'purpose',
    'status',
    'isDuplicate',
    'duplicateOfTransactionId',
  ];

  const filterPayloadEntries = (payload = {}) => {
    if (!payload || typeof payload !== "object") return [];
    const keys = Object.keys(payload);
    const hasRmName = keys.some(k => k === 'rmName' || k === 'assignedRmName');
    const isDepositPayload = Boolean(payload.transactionId && (payload.purpose || payload.virtualAccountNumber));

    const isVisible = (key, value) => {
      const lowerKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');

      if (
        lowerKey === 'previousstatus' ||
        lowerKey === 'updatedstatus' ||
        lowerKey === 'issuspicious' ||
        lowerKey === 'isinvestor' ||
        lowerKey === 'dealid' ||
        lowerKey === 'txnchannel' ||
        lowerKey === 'investorname'
      ) {
        return false;
      }
      if (value === null || value === undefined || value === '' || value === 'N/A') {
        return false;
      }

      if (
        lowerKey === 'investoruserid' ||
        lowerKey === 'invuserid' ||
        lowerKey === 'investorid' ||
        lowerKey === 'investoruser'
      ) {
        return false;
      }

      if (
        lowerKey === 'assigneeuserid' ||
        lowerKey === 'assigneduserid' ||
        lowerKey === 'assigneeid' ||
        lowerKey === 'assignedto'
      ) {
        return false;
      }

      if (
        lowerKey === 'entitytypeid' ||
        lowerKey === 'entitytype' ||
        lowerKey === 'businesstypeid' ||
        lowerKey === 'businesstype' ||
        lowerKey === 'investortypeid' ||
        lowerKey === 'usertypeid'
      ) {
        return false;
      }

      if (
        hasRmName &&
        (lowerKey === 'rmuserid' || lowerKey === 'rmid' || lowerKey === 'assignedrmuserid' || lowerKey === 'assignedrmid')
      ) {
        return false;
      }

      return true;
    };

    if (isDepositPayload) {
      return DEPOSIT_PAYLOAD_ORDER
        .filter((key) => Object.prototype.hasOwnProperty.call(payload, key) && isVisible(key, payload[key]))
        .map((key) => [key, payload[key]]);
    }

    return Object.entries(payload).filter(([key, value]) => isVisible(key, value));
  };

  const formatPayloadKey = (key) => {
    if (key === 'dealCategoryId') return 'Deal Category Name';
    if (key === 'busName' || key === 'businessName') return 'Business Name';
    if (key === 'businessTypeName' || key === 'businessType' || key === 'businessTypeId') return 'Business Type Name';
    if (key === 'rmName' || key === 'assignedRmName' || key === 'rmUserId' || key === 'rmId' || key === 'assignedRmUserId') return 'RM Name';
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
  };

  const getActionConfig = (action) => {
    switch (action) {
      case "NEW_ENQUIRY":
      case "ENQUIRY_CREATED": return { label: "Onboarding", color: "#e6ffed", textColor: "#22c55e" };
      case "COMPLETE_KYC": return { label: "KYC Completed", color: "#e6ffed", textColor: "#22c55e" };
      case "REVIEW_KYC": return { label: "KYC Reviewed", color: "#e6f7ff", textColor: "#1890ff" };
      case "ACTIVATE_USER": return { label: "User Activated", color: "#e6ffed", textColor: "#22c55e" };
      case "BUSINESS_PROFILE_UPDATED":
      case "INVESTOR_PROFILE_UPDATED": return { label: "Profile Update", color: "#e6f7ff", textColor: "#1890ff" };
      case "BUSINESS_DEACTIVATED":
      case "INVESTOR_DEACTIVATED": return { label: "Deactivation", color: "#fff0f6", textColor: "#eb2f96" };
      case "BUSINESS_BLACKLISTED":
      case "INVESTOR_BLACKLISTED": return { label: "Blacklist", color: "#fff1f0", textColor: "#f5222d" };
      case "BUSINESS_DELINQUENT":
      case "INVESTOR_DELINQUENT": return { label: "Delinquent", color: "#fffbe6", textColor: "#d4b106" };
      case "BUSINESS_REACTIVATED":
      case "INVESTOR_REACTIVATED": return { label: "Reactivation", color: "#e6ffed", textColor: "#22c55e" };
      case "RM_ASSIGNED": return { label: "RM Assigned", color: "#fff2e8", textColor: "#fa541c" };
      case "DEPOSIT_RECORDED": return { label: "Deposit Recorded", color: "#e6ffed", textColor: "#22c55e" };
      case "DEPOSIT_MARKED_DUPLICATE": return { label: "Marked Duplicate", color: "#fff1f0", textColor: "#f5222d" };
      default: return { label: action.replace(/^BUSINESS_|^INVESTOR_/, "").replace(/_/g, " "), color: "#e6f7ff", textColor: "#1890ff" };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const targetName = businessName || log.payload?.businessName || log.payload?.busName || "Business";
    const txnRef = log.payload?.transactionId;

    if (log.action === "DEPOSIT_RECORDED") {
      const txnNote = txnRef ? ` for transaction <strong>"${txnRef}"</strong>` : "";
      return `<strong>${name}</strong> (${role}) <strong>RECORDED DEPOSIT</strong>${txnNote} for business <strong>"${targetName}"</strong>`;
    }
    if (log.action === "DEPOSIT_MARKED_DUPLICATE") {
      const txnNote = txnRef ? ` for transaction <strong>"${txnRef}"</strong>` : "";
      return `<strong>${name}</strong> (${role}) <strong>MARKED DEPOSIT AS DUPLICATE</strong>${txnNote} for business <strong>"${targetName}"</strong>`;
    }

    if (log.action === "BUSINESS_PROFILE_UPDATED" || log.action === "INVESTOR_PROFILE_UPDATED") {
      return `<strong>${name}</strong> (${role}) <strong>UPDATED PROFILE</strong> for business <strong>"${targetName}"</strong>`;
    }
    if (log.action === "BUSINESS_DEACTIVATED" || log.action === "INVESTOR_DEACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>DEACTIVATED</strong> business <strong>"${targetName}"</strong>`;
    }
    if (log.action === "BUSINESS_BLACKLISTED" || log.action === "INVESTOR_BLACKLISTED") {
      return `<strong>${name}</strong> (${role}) <strong>BLACKLISTED</strong> business <strong>"${targetName}"</strong>`;
    }
    if (log.action === "BUSINESS_DELINQUENT" || log.action === "INVESTOR_DELINQUENT") {
      return `<strong>${name}</strong> (${role}) <strong>MARKED</strong> business <strong>"${targetName}"</strong> as <strong>DELINQUENT</strong>`;
    }
    if (log.action === "BUSINESS_REACTIVATED" || log.action === "INVESTOR_REACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>REACTIVATED</strong> business <strong>"${targetName}"</strong>`;
    }
    if (log.action === "RM_ASSIGNED") {
      const rm = log.payload?.rmName || "Relationship Manager";
      return `<strong>${name}</strong> (${role}) <strong>ASSIGNED RELATIONSHIP MANAGER</strong> <strong>"${rm}"</strong> to <strong>"${targetName}"</strong>`;
    }

    const actionConfig = getActionConfig(log.action);
    return `<strong>${name}</strong> (${role}) <strong>PERFORMED ${actionConfig.label.toUpperCase()}</strong> on business <strong>"${targetName}"</strong>`;
  };

  return (
    <div style={{ padding: "24px 0", maxWidth: "900px", margin: "0 auto", fontFamily: "Inter, sans-serif" }}>
      <div style={{ border: "1px solid #f0f0f0", borderRadius: "8px", overflow: "hidden", background: "#ffffff" }}>
        
        <div 
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            padding: "16px 24px", 
            background: "#fafafa", 
            borderBottom: isCollapsed ? "none" : "1px solid #f0f0f0",
            cursor: "pointer",
            userSelect: "none"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#262626" }}>Business Lifecycle History</span>
            <span style={{ 
              background: "#e8e8e8", 
              color: "#595959", 
              fontSize: "12px", 
              fontWeight: "600", 
              padding: "2px 8px", 
              borderRadius: "4px" 
            }}>
              {totalRecords}
            </span>
          </div>
          <div>
            {isCollapsed ? <DownOutlined style={{ fontSize: "14px", color: "#595959" }} /> : <UpOutlined style={{ fontSize: "14px", color: "#595959" }} />}
          </div>
        </div>

        {!isCollapsed && (
          <div style={{ padding: "24px 24px 8px 24px" }}>
            <Spin spinning={loading}>
              {logs?.length === 0 ? (
                <div style={{ textAlign: "center", color: "#8c8c8c", padding: "20px 0" }}>No activity logs found.</div>
              ) : (
                logs?.map((log, index) => {
                  const isLast = index === logs.length - 1;
                  const actionConfig = getActionConfig(log.action);
                  const dateStr = dayjs(log.createdAt).format("DD MMM YYYY");
                  const timeStr = dayjs(log.createdAt).format("hh:mm A");

                  const payloadEntries = filterPayloadEntries(log.payload);

                  return (
                    <div key={log.logId || index} style={{ display: "flex", minHeight: "80px" }}>
                      
                      <div style={{ width: "90px", textAlign: "right", paddingRight: "20px", flexShrink: 0 }}>
                        <div style={{ fontSize: "12px", fontWeight: "500", color: "#262626" }}>{dateStr}</div>
                        <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "2px" }}>{timeStr}</div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "20px", flexShrink: 0 }}>
                        <div style={{ 
                          width: "16px", 
                          height: "16px", 
                          borderRadius: "50%", 
                          border: `3px solid ${actionConfig.textColor}`, 
                          background: "#ffffff", 
                          display: "flex", 
                          justifyContent: "center", 
                          alignItems: "center",
                          boxSizing: "border-box"
                        }}>
                          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: actionConfig.textColor }} />
                        </div>

                        {!isLast && (
                          <div style={{ 
                            width: "3px", 
                            background: actionConfig.textColor, 
                            flexGrow: 1, 
                            marginTop: "4px", 
                            marginBottom: "4px" 
                          }} />
                        )}
                      </div>

                      <div style={{ paddingLeft: "20px", paddingBottom: "24px", flexGrow: 1 }}>
                        <div 
                          style={{ fontSize: "13px", color: "#434343", lineHeight: "1.5" }}
                          dangerouslySetInnerHTML={{ __html: formatDesc(log) }}
                        />
                        
                        <div style={{ display: "flex", alignItems: "center", gap: "24px", marginTop: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Action:</span>
                            <span style={{ 
                              background: actionConfig.color, 
                              color: actionConfig.textColor, 
                              fontSize: "11px", 
                              fontWeight: "700", 
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              padding: "2px 8px", 
                              borderRadius: "4px" 
                            }}>
                              {actionConfig.label}
                            </span>
                          </div>
                        </div>

                        {payloadEntries.length > 0 && (
                          <div style={{ 
                            marginTop: 10, 
                            padding: "10px 16px", 
                            background: "#fafafa", 
                            border: "1px solid #f0f0f0", 
                            borderRadius: "6px",
                            fontSize: "12px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.01)"
                          }}>
                            {payloadEntries.map(([key, value]) => (
                              <div key={key} style={{ marginBottom: "6px" }}>
                                <span style={{ fontWeight: "600", color: "#595959" }}>{formatPayloadKey(key)}:</span>{" "}
                                <span style={{ color: "#262626" }}>{formatPayloadValue(key, value, log.payload)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                    </div>
                  );
                })
              )}
            </Spin>
            {totalRecords > limit && (
              <div style={{ textAlign: "right", marginTop: 10 }}>
                <Pagination
                  current={page}
                  total={totalRecords}
                  pageSize={limit}
                  onChange={(p) => setPage(p)}
                  size="small"
                />
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default BusinessActivityLog;
